<template>
  <div class="backup-summary-tiles mb-4">
    <v-card
      v-for="tile in tiles"
      :key="tile.id"
      class="backup-summary-tiles__tile pa-6"
      :class="{ 'backup-summary-tiles__tile--clickable': tile.clickable }"
      :data-testid="`backup-summary-tile-${tile.id}`"
      rounded="lg"
      elevation="0"
      v-bind="tile.clickable ? { onClick: () => $emit('tile-clicked', tile.id) } : {}"
    >
      <v-skeleton-loader
        v-if="loading"
        type="heading, text"
        class="bg-transparent"
        :data-testid="`backup-summary-tile-${tile.id}-loader`"
      />
      <div v-else class="d-flex align-center justify-space-between">
        <div>
          <div class="d-flex align-center ga-2">
            <v-icon
              :icon="tile.icon"
              color="primary"
              size="45"
              :data-testid="`backup-summary-tile-${tile.id}-icon`"
            />
            <span class="big-number" :class="tile.valueClass">{{ tile.value }}</span>
          </div>
          <div class="subtitle2 mt-1">{{ tile.label }}</div>
          <div
            v-if="tile.caption"
            class="caption text-indigo-medium"
            :data-testid="`backup-summary-tile-${tile.id}-caption`"
          >
            {{ tile.caption }}
          </div>
        </div>
        <!-- Only a tile that leads somewhere gets a chevron. -->
        <v-icon
          v-if="tile.clickable"
          icon="$chevronRight"
          color="link"
          size="16"
          :data-testid="`backup-summary-tile-${tile.id}-chevron`"
        />
      </div>
    </v-card>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment";
import { BackupSummaryTile } from "@/constants/cloud-backup.ts";

export default defineComponent({
  props: {
    totalBackups: {
      type: Number,
      default: 0,
    },
    itemsRestored: {
      type: Number,
      default: 0,
    },
    // Unix ms of the most recent run in each direction; 0 when that job never ran, in
    // which case the tile carries no caption.
    lastBackupTime: {
      type: Number,
      default: 0,
    },
    lastRestoreTime: {
      type: Number,
      default: 0,
    },
    backedUpUsers: {
      type: Number,
      default: 0,
    },
    seatLimit: {
      type: Number,
      default: 0,
    },
    connectorsNeedingAttention: {
      type: Number,
      default: 0,
    },
    disconnectedConnectors: {
      type: Number,
      default: 0,
    },
    totalConnectors: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["tile-clicked"],
  setup(props) {
    const i18n = useI18n();

    // "Last backup 2 days ago" — relative, the same way the dashboard widget reports the
    // very same timestamps. Nothing to say until that direction has run at least once.
    const lastRunCaption = (key: string, time: number): string =>
      time ? i18n.t(key, { time: moment(time).fromNow() }) : "";

    const seatLimitCaption = computed(() =>
      i18n.t("cloudBackupAndRestore.overview.seatLimit", {
        limit: props.seatLimit || i18n.t("general.unlimited"),
      })
    );

    const tiles = computed(() => [
      {
        id: BackupSummaryTile.TOTAL_BACKUPS,
        icon: "$backup",
        value: props.totalBackups,
        label: i18n.t("cloudBackupAndRestore.overview.totalBackups"),
        caption: lastRunCaption("cloudBackupAndRestore.overview.lastBackup", props.lastBackupTime),
        valueClass: "text-primary",
        clickable: false,
      },
      {
        id: BackupSummaryTile.ITEMS_RESTORED,
        icon: "$restore",
        value: props.itemsRestored,
        label: i18n.t("cloudBackupAndRestore.overview.itemsRestored"),
        caption: lastRunCaption(
          "cloudBackupAndRestore.overview.lastRestore",
          props.lastRestoreTime
        ),
        valueClass: "text-primary",
        clickable: false,
      },
      {
        id: BackupSummaryTile.BACKED_UP_USERS,
        icon: "$usersS",
        value: props.backedUpUsers,
        label: i18n.t("cloudBackupAndRestore.overview.backedUpUsers"),
        caption: seatLimitCaption.value,
        valueClass: "text-primary",
        clickable: false,
      },
      {
        id: BackupSummaryTile.NEEDS_ATTENTION,
        icon: "$thirdPartyApp",
        value: props.connectorsNeedingAttention,
        label: i18n.t("cloudBackupAndRestore.overview.needsAttention"),
        caption: props.disconnectedConnectors
          ? i18n.t("cloudBackupAndRestore.overview.notConnectedCaption", {
              count: props.disconnectedConnectors,
              total: props.totalConnectors,
            })
          : "",
        valueClass: props.connectorsNeedingAttention ? "text-red-base" : "text-primary",
        // The only tile that leads anywhere: the connected apps tab is where an admin
        // reconnects an app or looks into an unhealthy one.
        clickable: true,
      },
    ]);

    return {
      tiles,
    };
  },
});
</script>

<style lang="scss" scoped>
.backup-summary-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  &__tile {
    background-color: rgb(var(--v-theme-white));

    &--clickable {
      cursor: pointer;
    }
  }
}

@media only screen and (max-width: 960px) {
  .backup-summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
